import {
  collection,
  deleteField,
  doc,
  DocumentData,
  FieldValue,
  UpdateData
} from 'firebase/firestore';
import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useNovelBatch } from './useNovelRepository/batch';
import { useNovelRefs } from './useNovelRepository/refs';

import type {
  AnySummary,
  Character,
  EntitySummary,
  EntityType,
  Event,
  EventSummary,
  Location,
  NarrativeSummary,
  NarrativeType,
  Novel,
  Organization,
  SummaryType,
} from '@/lib/types';

import { ContentDao } from '@/api/daos/content-dao';
import { EntityDao } from '@/api/daos/entity-dao';
import { NarrativeDao } from '@/api/daos/narrative-dao';
import { NotesDao } from '@/api/daos/notes-dao';
import { NovelDao } from '@/api/daos/novel-dao';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { toTimestamp } from '@/lib/date-utils';
import { logger } from '@/lib/logger';



type ValidFamily = 'entities' | 'narrative';

export function useNovelRepository() {
  const { t } = useTranslation();
  const { firestore, backend } = useFirebase();
  const { setSelectedNovelId, selectedNovelId, novel, isExampleNovel } = useNovel();
  const { userData } = useUserProfile();

  const isReadOnlyExample = isExampleNovel && !userData?.isAdmin;

  const novelDao = useMemo(() => new NovelDao(firestore), [firestore]);
  const entityDao = useMemo(() => new EntityDao(firestore), [firestore]);
  const narrativeDao = useMemo(() => new NarrativeDao(firestore), [firestore]);
  const notesDao = useMemo(() => new NotesDao(firestore), [firestore]);
  const contentDao = useMemo(() => new ContentDao(firestore, (ownerId, novelId) => backend.novels.synchronizePublication(ownerId, novelId)), [firestore, backend.novels]);

  const { getItemRef, getCollectionRef, getBucketRef } = useNovelRefs(novel, novelDao);

  const { addSaveToBatch, addSummaryUpdateToBatch } = useNovelBatch(novel, novelDao, firestore, getItemRef);

  const getNewId = useCallback(() => doc(collection(firestore, '_tmp')).id, [firestore]);

  const summariesArrays = useMemo(() => {
    const result: Partial<Record<SummaryType, AnySummary[]>> = {};

    // 2. Process all other types (characters, locations, etc) sorting them by recently updated
    for (const [type, records] of Object.entries(novel.summaries)) {
      if (records && !result[type as SummaryType]) {
        const arr = Object.values(records as Record<string, AnySummary>);
        arr.sort((a, b) => toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt));
        result[type as SummaryType] = arr;
      }
    }

    // 1. Process narrative/chronological types explicitly so TypeScript knows they have an 'order' property
    result.events = Object.values(novel.summaries.events).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    result.chapters = Object.values(novel.summaries.chapters).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    result.scenes = Object.values(novel.summaries.scenes).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    result.arcs = Object.values(novel.summaries.arcs).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return result;
  }, [novel.summaries]);

  const getSummaries = useCallback(
    <T extends AnySummary = AnySummary>(type: SummaryType): T[] => {
      return (summariesArrays[type] as T[]) || [];
    },
    [summariesArrays]
  );

  const handleExampleWrite = useCallback(
    async <T>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } catch (err) {
        if (isReadOnlyExample) {
          throw new Error(t('example_novel.readonly_hint'));
        }
        throw err;
      }
    },
    [isReadOnlyExample, t]
  );

  const saveItem = useCallback(
    async <T extends { id: string }>(family: ValidFamily, type: string, item: T) => {
      if (!novel.id) throw new Error('Novel not loaded');
      await handleExampleWrite(async () => {
        await novelDao.setItem(novel.ownerId, novel, family, type, item);
        if (family === 'narrative' && type === 'chapters') await backend.novels.synchronizePublication(novel.ownerId, novel.id);
      });
    },
    [backend.novels, novelDao, novel, handleExampleWrite]
  );

  const deleteItem = useCallback(
    async (family: ValidFamily, type: string, id: string) => {
      if (!novel.id) throw new Error('Novel not loaded');
      await handleExampleWrite(async () => {
        await novelDao.deleteItem(novel.ownerId, novel, family, type, id);
        if (family === 'narrative' && type === 'chapters') await backend.novels.synchronizePublication(novel.ownerId, novel.id);
      });
    },
    [backend.novels, novelDao, novel, handleExampleWrite]
  );

  const batchSaveItems = useCallback(
    async <T extends { id: string }>(family: ValidFamily, type: string, items: T[]) => {
      if (!novel.id) throw new Error('Novel not loaded');
      await handleExampleWrite(async () => {
        await novelDao.batchSaveItems(novel.ownerId, novel, family, type, items);
        if (family === 'narrative' && type === 'chapters') await backend.novels.synchronizePublication(novel.ownerId, novel.id);
      });
    },
    [backend.novels, novelDao, novel, handleExampleWrite]
  );

  const batchSaveItemsNonBlocking = useCallback(
    <T extends { id: string }>(family: ValidFamily, type: string, items: T[]) => {
      if (!novel.id) return;
      novelDao.batchSaveItems(novel.ownerId, novel, family, type, items)
        .then(() => family === 'narrative' && type === 'chapters'
          ? backend.novels.synchronizePublication(novel.ownerId, novel.id)
          : undefined)
        .catch(err => {
          logger.error('Non-blocking batch save failed:', err);
        });
    },
    [backend.novels, novelDao, novel]
  );

  const updateNovelItem = useCallback(
    async (updates: Partial<Novel>) => {
      if (!novel.id) throw new Error('Novel not loaded');
      return handleExampleWrite(() => backend.novels.updateNovel(novel.ownerId, novel.id, updates));
    },
    [backend.novels, novel, handleExampleWrite]
  );

  const saveEntity = useCallback(
    async (type: EntityType, entity: Character | Location | Organization | Event) => {
      if (!novel.id) throw new Error('Novel not loaded');
      return handleExampleWrite(() => entityDao.saveEntity(novel.ownerId, novel, type, entity));
    },
    [entityDao, novel, handleExampleWrite]
  );

  const updateEntitySummary = useCallback(
    async (type: EntityType, entityId: string, summary: EntitySummary | EventSummary | null) => {
      if (!novel.id) throw new Error('Novel not loaded');
      const updates: UpdateData<DocumentData> | FieldValue = summary ? { ...summary } : deleteField();
      return handleExampleWrite(() => entityDao.updateEntity(novel.ownerId, novel, type, entityId, updates));
    },
    [entityDao, novel, handleExampleWrite]
  );

  const updateAllEventSummaries = useCallback(
    async (summaries: Record<string, EventSummary>) => {
      if (!novel.id) throw new Error('Novel not loaded');
      return handleExampleWrite(() => entityDao.batchUpdateSummaries(
        novel.ownerId,
        novel,
        'entities',
        'events',
        summaries
      ));
    },
    [entityDao, novel, handleExampleWrite]
  );

  const updateNarrativeSummary = useCallback(
    async (type: NarrativeType, nodeId: string, summary: NarrativeSummary | null) => {
      if (!novel.id) throw new Error('Novel not loaded');
      const updates: UpdateData<DocumentData> | FieldValue = summary
        ? {
          ...summary,
          updatedAt: new Date().toISOString(),
        }
        : deleteField();
      await handleExampleWrite(async () => {
        await narrativeDao.updateNarrativeItem(novel.ownerId, novel, type, nodeId, updates);
        if (type === 'chapters') await backend.novels.synchronizePublication(novel.ownerId, novel.id);
      });
    },
    [backend.novels, narrativeDao, novel, handleExampleWrite]
  );

  const updateEntityItem = useCallback(
    async (type: EntityType, entityId: string, updates: UpdateData<DocumentData> | FieldValue) => {
      if (!novel.id) throw new Error('Novel not loaded');
      return handleExampleWrite(() => entityDao.updateEntity(novel.ownerId, novel, type, entityId, updates));
    },
    [entityDao, novel, handleExampleWrite]
  );

  const updateNarrativeItem = useCallback(
    async (type: NarrativeType, nodeId: string, updates: UpdateData<DocumentData> | FieldValue) => {
      if (!novel.id) throw new Error('Novel not loaded');
      await handleExampleWrite(async () => {
        await narrativeDao.updateNarrativeItem(novel.ownerId, novel, type, nodeId, updates);
        if (type === 'chapters') await backend.novels.synchronizePublication(novel.ownerId, novel.id);
      });
    },
    [backend.novels, narrativeDao, novel, handleExampleWrite]
  );

  return {
    novel,
    selectedNovelId,
    setSelectedNovelId,
    firestore,

    entityDao,
    narrativeDao,
    notesDao,
    contentDao,
    novelDao,

    getItemRef,
    getCollectionRef,
    getBucketRef,

    getNewId,
    getSummaries,

    saveItem,
    saveEntity,
    deleteItem,
    batchSaveItems,
    updateNovelItem,

    updateEntitySummary,
    updateAllEventSummaries,
    updateNarrativeSummary,

    addSaveToBatch,
    addSummaryUpdateToBatch,

    updateEntityItem,
    updateNarrativeItem,
    batchSaveItemsNonBlocking,
  };
}
